/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import searchIcon from '../../assets/icons/search.svg';
import downloadIcon from '../../assets/icons/download-master.svg';
import reloadIcon from '../../assets/icons/svg/reload-vector.svg';
import viewIcon from '../../assets/view.svg';
import { DataGrid } from "../../common";
import { APIEndpoint, DFPAPIEndpoint } from "../../utils/axios";
import { PageSizes } from "../../utils/configs";
import moment from "moment";
import { NotificationDetailModal } from './NotificationDetailModal';
import { getEventTitle } from './utils/getEventTitle';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Columns = [
  {
    field: 'createdAt',
    headerName: 'Created At',
    suppressSizeToFit: true,
    resizable: true,
    minWidth: 150,
    valueGetter: (params) => {
      if (!params.data?.createdAt) return '';
      
      const notificationDate = moment(params.data.createdAt);
      const now = moment();
      const daysDiff = now.diff(notificationDate, 'days');
      
      // If more than 7 days ago, show formatted date
      if (daysDiff > 7) {
        return notificationDate.format('DD-MM-YY (HH:mm)');
      }
      
      // Otherwise show relative time (e.g., "5 mins ago", "3 days ago")
      return notificationDate.fromNow();
    }
  },
  {
    field: 'priority',
    headerName: 'Priority',
    suppressSizeToFit: true,
    resizable: true,
    minWidth: 90,
    cellStyle: (params) => {
      const priority = params.data?.priority?.toUpperCase();
      let color = '#e0e0e0'; // Default color
      
      if (priority === 'HIGH' || priority === 'CRITICAL') {
        color = '#dc3545'; // Red
      } else if (priority === 'MEDIUM') {
        color = '#ffc107'; // Dark Yellow
      } else if (priority === 'LOW') {
        color = '#28a745'; // Green
      }
      
      return {
        color: color,
        fontWeight: '600',
      };
    }
  },
  {
    field: 'type',
    headerName: 'Event Type',
    suppressSizeToFit: true,
    resizable: true,
    flex: 1,
    minWidth: 180,
    tooltipField: 'type',
    cellStyle: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    valueGetter: (params) => {
      return getEventTitle(params.data?.type);
    }
  },
  {
    field: 'title',
    headerName: 'Title',
    suppressSizeToFit: true,
    resizable: true,
    flex: 2,
    minWidth: 300,
    tooltipField: 'title',
    cellStyle: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }
  },

  {
    field: 'actions',
    headerName: 'Actions',
    suppressSizeToFit: false,
    resizable: false,
    cellRenderer: 'ActionCell',
  }
];

export const EventNotifications = (props) => {
  const { t, ready } = useTranslation();
  const [pageLimit, setPageLimit] = useState(50);
  const [searchKey, setSearchKey] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [isReloadTableData, setIsReloadTableData] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [actionItems, setActionItems] = useState([]);
  const [selectedNotification, setSelectedNotification] = useState(null);
  
  const sType = localStorage.getItem('sType');
  const DOMAIN = (sType === 'dfp' ? DFPAPIEndpoint : APIEndpoint);

  useEffect(() => {
    setActionItems([
      {
        title: '',
        attribute: 'View',
      },
      {
        title: '',
        attribute: 'Download',
      }
    ]);
    setIsReloadTableData(true);
  }, []);

  // Debounce search input
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setSearchKey(searchInput);
    }, 500); // 500ms delay

    return () => {
      clearTimeout(debounceTimer);
    };
  }, [searchInput]);

  const fetchNotifications = async (page = 1, perPage = 50) => {
    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: perPage.toString(),
      });

      if (searchKey) {
        queryParams.append('search', searchKey);
      }
      
      const res = await DOMAIN.get(`event-notifications?${queryParams.toString()}`);

      console.log(res);
 return {items: res.data.notifications, count: res.data.totalCount};
    } catch (error) {
      console.error('Error fetching notifications:', error);
      return { items: [], count: 0 };
    }
  };

  const onPerLimitChange = (e) => {
    setPageLimit(Number(e.target.value));
  };

  const onSearch = (e) => {
    setSearchInput(e.target.value);
  };

  const onRefresh = () => {
    setIsReloadTableData(true);
    setTimeout(() => {
      setIsReloadTableData(false);
    }, 100);
  };

  const getActionCellIcons = (cellDef, data) => {
    // Show view button for all notifications
    if (cellDef.attribute === 'View') {
      return [
        {
          src: viewIcon,
          value: "View",
          title: "View Details"
        }
      ];
    }
    
    // Only show download button for DOWNLOAD_STATUS type notifications
    if (cellDef.attribute === 'Download' && data?.type === 'DOWNLOAD_STATUS') {
      const isDownloading = downloadingId === data._id;
      return [
        {
          src: downloadIcon,
          value: "Download",
          title: isDownloading ? "Downloading..." : "Download"
        }
      ];
    }
    return [];
  };

  const onAction = async (e, action, selectedRowData) => {
    if (action.value === 'View') {
      setSelectedNotification(selectedRowData);
    } else if (action.value === 'Download' && selectedRowData?.metadata?.jobId) {
      await handleDownload(selectedRowData);
    }
  };

  const handleCloseModal = () => {
    setSelectedNotification(null);
  };

  const handleDownload = async (notification) => {
    const jobId = notification.metadata?.jobId;
    
    if (!jobId) {
      console.error('No job ID found in notification');
      return;
    }

    setDownloadingId(notification._id);

    try {
      // Check download status
      const dowId = await DOMAIN.get(`downloads/status/${jobId}`);
      
      if (dowId.status === "completed") {
        // Download the file
        const fileData = await DOMAIN.get(
          `downloads/analytics/${dowId.fileName}${dowId.fileExtension}`
        );
        
        const url = window.URL.createObjectURL(new Blob([fileData]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${dowId.fileName}${dowId.fileExtension}`);
        document.body?.appendChild(link);
        link.click();
        document.body?.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        setDownloadingId(null);
      } else {
        setDownloadingId(null);
        toast.error('File not found', {
          position: toast.POSITION.TOP_CENTER,
          hideProgressBar: true,
          pauseOnHover: true,
          draggable: false,
          autoClose: 3800,
          style: {
            backgroundColor: '#d9534f',
            color: '#fff',
            borderRadius: '8px',
            fontSize: '1rem',
            width:'30rem'
          }
        });
      }
    } catch (error) {
      setDownloadingId(null);
      toast.error('Error downloading file', {
        position: toast.POSITION.TOP_CENTER,
        hideProgressBar: true,
        pauseOnHover: true,
        draggable: false,
        autoClose: 3800,
      });
    }
  };

  return (
    <div className="flexRow full p-0 m-0">
      <div className='full-flex flexCol width100 p-0 m-0'>
        <div className="op-aircraft-container flexCol full-flex ag-theme-alpine-dark">
          <div className='flexRow width100 margBot10 margTop8 justifyContentSpaceBetween'>
            <div className='flexRow alignItemsCenter'>
              <button className='page-button'>
                <select 
                  className="darkBlue-bg" 
                  onChange={onPerLimitChange} 
                  value={pageLimit} 
                  id="page-size"
                >
                  {PageSizes.map((_) => {
                    return (
                      <option key={_.value} value={_.value}>{_.label}</option>
                    )
                  })}
                </select>
              </button>
              
              <button 
                className='page-button margLeft8' 
                onClick={onRefresh}
                title="Refresh notifications"
                style={{ padding: '8px 12px', cursor: 'pointer' }}
              >
                <img 
                  src={reloadIcon} 
                  alt="Refresh" 
                  style={{ width: '16px', height: '16px' }}
                />
              </button>
            </div>

            <div className="mt-2 underline">
              <span>
                <img 
                  className="icon-size" 
                  alt={t('filters.searchText') || 'Search'} 
                  src={searchIcon} 
                />
              </span>
              <span>
                <input 
                  className="search" 
                  onChange={onSearch} 
                  type="search" 
                  placeholder={t('filters.searchText') || 'Search'} 
                  value={searchInput}
                />
              </span>
            </div>
          </div>

          <DataGrid
            className="full-flex"
            columns={Columns}
            initialPageLimit={pageLimit}
            showDefaultFilters={false}
            searchKey={searchKey}
            getRows={fetchNotifications}
            noResultsMessage={"No notifications found"}
            resetDataSource={isReloadTableData}
            columnResize={true}
            actions={actionItems}
            getActionCellIcons={getActionCellIcons}
            onAction={onAction}
          />

          {!ready && <div className='overlay_hide sub'></div>}
        </div>
      </div>

      {selectedNotification && (
        <NotificationDetailModal 
          notification={selectedNotification}
          onClose={handleCloseModal}
        />
      )}
      <ToastContainer />
    </div>
  );
};